import React, { useState, useEffect } from 'react';
import { Chart } from 'primereact/chart';
import { Card } from 'primereact/card';
import {
  MonthlyTrendChartProps,
  PredictionAccuracy,
  MonthlyChartDataItem,
  ChartData,
  ChartOptions,
} from './types';
import { TailSpin } from 'react-loader-spinner';

const MonthlyTrendChart: React.FC<MonthlyTrendChartProps> = ({
  predictionTypeId,
  league,
  country,
  seasonYear,
}) => {
  const [data, setData] = useState<PredictionAccuracy[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [predictionTypeName, setPredictionTypeName] = useState<string>('');
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [chartOptions, setChartOptions] = useState<ChartOptions | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, [predictionTypeId, league, country, seasonYear]);

  useEffect(() => {
    if (chartData) {
      customizeChartLabels();
    }
  }, [chartData]);

  const customizeChartLabels = () => {
    setTimeout(() => {
      const chart = document.querySelector(
        '.monthly-trend-chart .p-chart',
      ) as HTMLCanvasElement;
      if (chart) {
        try {
          const chartInstance = (chart as any).__chartjs__;
          if (chartInstance && chartInstance.options) {
            if (
              chartInstance.options.plugins &&
              chartInstance.options.plugins.legend &&
              chartInstance.options.plugins.legend.labels
            ) {
              chartInstance.options.plugins.legend.labels.color = '#FFFFFF';

              if (isMobile) {
                chartInstance.options.plugins.legend.labels.font = {
                  size: 10,
                };
              }
            }

            if (chartInstance.options.scales) {
              if (
                chartInstance.options.scales.y &&
                chartInstance.options.scales.y.title
              ) {
                chartInstance.options.scales.y.title.color = '#FFFFFF';
                chartInstance.options.scales.y.ticks =
                  chartInstance.options.scales.y.ticks || {};
                chartInstance.options.scales.y.ticks.color = '#FFFFFF';

                if (isMobile) {
                  chartInstance.options.scales.y.ticks.font = { size: 9 };
                  chartInstance.options.scales.y.title.font = { size: 10 };
                }
              }

              if (
                chartInstance.options.scales.x &&
                chartInstance.options.scales.x.title
              ) {
                chartInstance.options.scales.x.title.color = '#FFFFFF';
                chartInstance.options.scales.x.ticks =
                  chartInstance.options.scales.x.ticks || {};
                chartInstance.options.scales.x.ticks.color = '#FFFFFF';

                if (isMobile) {
                  chartInstance.options.scales.x.ticks.font = { size: 9 };
                  chartInstance.options.scales.x.title.font = { size: 10 };
                  chartInstance.options.scales.x.ticks.maxRotation = 45;
                  chartInstance.options.scales.x.ticks.minRotation = 45;
                }
              }
            }

            chartInstance.update();
          }
        } catch (e) {
          console.warn('Unable to customize chart colors:', e);
        }
      }
    }, 100);
  };

  const fetchData = async (): Promise<void> => {
    try {
      setLoading(true);
      const startTime = Date.now();

      const apiUrl = 'https://betzen.app/api';
      // const apiUrl = 'https://localhost:7014/api';
      let url = `${apiUrl}/predictionaccuracy/monthly-trend`;
      const params = new URLSearchParams();

      console.log('Monthly Trend - PredictionTypeId:', predictionTypeId);
      console.log('Monthly Trend - League:', league);
      console.log('Monthly Trend - Country:', country);
      console.log('Monthly Trend - SeasonYear:', seasonYear);

      if (predictionTypeId && predictionTypeId !== 0) {
        params.append('predictionTypeId', predictionTypeId.toString());
      }

      if (league && typeof league === 'string' && league.trim() !== '') {
        params.append('league', league.trim());
      }

      if (country && typeof country === 'string' && country.trim() !== '') {
        params.append('country', country.trim());
      }

      if (
        seasonYear &&
        typeof seasonYear === 'string' &&
        seasonYear.trim() !== ''
      ) {
        params.append('seasonYear', seasonYear.trim());
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      console.log('Final URL for monthly trend:', url);

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const responseData: PredictionAccuracy[] = await response.json();

      // Calculate how much time has passed
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 400 - elapsedTime);

      // If less than 1 second has passed, wait for the remaining time
      if (remainingTime > 0) {
        await new Promise((resolve) => setTimeout(resolve, remainingTime));
      }

      if (responseData.length === 0) {
        setError('No data available for the selected filters');
        setLoading(false);
        return;
      }

      if (
        responseData.length > 0 &&
        predictionTypeId &&
        predictionTypeId !== 0
      ) {
        setPredictionTypeName(responseData[0].predictionType || 'Unknown Type');
      } else {
        setPredictionTypeName('All Prediction Types');
      }

      const sortedData = [...responseData].sort((a, b) => {
        if (!a.yearMonth || !b.yearMonth) return 0;
        return a.yearMonth.localeCompare(b.yearMonth);
      });

      setData(sortedData);
      prepareChartData(sortedData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching monthly trend data:', err);
      setError('Error loading data. Please try again later.');
      setLoading(false);
    }
  };

  const prepareChartData = (sortedData: PredictionAccuracy[]): void => {
    if (!sortedData || sortedData.length === 0) return;

    const dataByMonth: Record<
      string,
      Record<string, { accuracy: number; predictions: number }>
    > = {};

    sortedData.forEach((item) => {
      if (!item.yearMonth || !item.predictionType) return;

      if (!dataByMonth[item.yearMonth]) {
        dataByMonth[item.yearMonth] = {};
      }

      if (!dataByMonth[item.yearMonth][item.predictionType]) {
        dataByMonth[item.yearMonth][item.predictionType] = {
          accuracy: 0,
          predictions: 0,
        };
      }

      const currentEntry = dataByMonth[item.yearMonth][item.predictionType];
      const totalPredictions = currentEntry.predictions + item.totalPredictions;
      const weightedAccuracy =
        (currentEntry.accuracy * currentEntry.predictions +
          item.accuracyPercentage * item.totalPredictions) /
        totalPredictions;

      currentEntry.accuracy = weightedAccuracy;
      currentEntry.predictions += item.totalPredictions;
    });

    const months = Object.keys(dataByMonth).sort();

    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const fullMonthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    const displayMonths = isMobile
      ? months.map((month) => {
          const parts = month.split('-');
          if (parts.length !== 2) return month;

          const monthNum = parseInt(parts[1]);
          const monthName = monthNames[monthNum - 1] || parts[1];

          const yearShort = parts[0].substring(2);

          return `${monthName} ${yearShort}`;
        })
      : months.map((month) => {
          const parts = month.split('-');
          if (parts.length !== 2) return month;

          const monthNum = parseInt(parts[1]);
          const monthName = fullMonthNames[monthNum - 1] || parts[1];

          return `${monthName} ${parts[0]}`;
        });

    const predictionTypes = new Set<string>();
    Object.values(dataByMonth).forEach((monthData) => {
      Object.keys(monthData).forEach((type) => predictionTypes.add(type));
    });

    if (predictionTypeId && predictionTypeId !== 0) {
      const chartData: ChartData = {
        labels: displayMonths,
        datasets: [
          {
            label: 'Accuracy (%)',
            data: months.map((month) => {
              const monthData = dataByMonth[month];
              if (!monthData) return 0;

              const predType = Object.keys(monthData)[0];
              if (!predType) return 0;

              return parseFloat(monthData[predType].accuracy.toFixed(2));
            }),
            fill: false,
            borderColor: '#2196F3',
            backgroundColor: 'rgba(33, 150, 243, 0.2)',
            tension: 0.4,
          },
        ],
      };

      const chartOptions: ChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top',
            labels: {
              font: {
                size: isMobile ? 10 : 14,
              },
              color: '#FFFFFF',
            },
          },
          tooltip: {
            callbacks: {
              afterLabel: function (context: any) {
                const monthIndex = context.dataIndex;
                const month = months[monthIndex];
                const monthData = dataByMonth[month];
                if (!monthData) return '';

                const predType = Object.keys(monthData)[0];
                if (!predType) return '';

                return `Predictions: ${monthData[predType].predictions}`;
              },
            },
          },
        },
        scales: {
          y: {
            min: 0,
            max: 100,
            title: {
              display: true,
              text: isMobile ? 'Acc. (%)' : 'Accuracy (%)',
              font: {
                size: isMobile ? 10 : 12,
              },
              color: '#FFFFFF',
            },
            ticks: {
              font: {
                size: isMobile ? 9 : 11,
              },
              color: '#FFFFFF',
            },
          },
          x: {
            title: {
              display: !isMobile,
              text: 'Month',
              font: {
                size: isMobile ? 10 : 12,
              },
              color: '#FFFFFF',
            },
            ticks: {
              font: {
                size: isMobile ? 9 : 11,
              },
              maxRotation: isMobile ? 45 : 0,
              minRotation: isMobile ? 45 : 0,
              color: '#FFFFFF',
            },
          },
        },
      };

      setChartData(chartData);
      setChartOptions(chartOptions);
    } else {
      const predTypeArray = Array.from(predictionTypes);
      const colors = [
        '#42A5F5',
        '#66BB6A',
        '#FFA726',
        '#26C6DA',
        '#7E57C2',
        '#EC407A',
        '#AB47BC',
        '#5C6BC0',
        '#29B6F6',
        '#26A69A',
      ];

      const datasets = predTypeArray.map((type, index) => {
        const color = colors[index % colors.length];

        return {
          label:
            isMobile && type.length > 10 ? type.substring(0, 10) + '...' : type,
          data: months.map((month) => {
            const monthData = dataByMonth[month];
            if (!monthData || !monthData[type]) return 0;
            return parseFloat(monthData[type].accuracy.toFixed(2));
          }),
          fill: false,
          borderColor: color,
          backgroundColor: color.replace(')', ', 0.2)').replace('rgb', 'rgba'),
          tension: 0.4,
        };
      });

      const chartData: ChartData = {
        labels: displayMonths,
        datasets: datasets as any,
      };

      const chartOptions: ChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top',
            labels: {
              font: {
                size: isMobile ? 9 : 14,
              },
              boxWidth: isMobile ? 10 : 40,
              color: '#FFFFFF',
            },
          },
          tooltip: {
            callbacks: {
              afterLabel: function (context: any) {
                const monthIndex = context.dataIndex;
                const month = months[monthIndex];
                const datasetIndex = context.datasetIndex;
                const type = predTypeArray[datasetIndex];

                const monthData = dataByMonth[month];
                if (!monthData || !monthData[type]) return '';

                return `Predictions: ${monthData[type].predictions}`;
              },
            },
          },
        },
        scales: {
          y: {
            min: 0,
            max: 100,
            title: {
              display: true,
              text: isMobile ? 'Acc. (%)' : 'Accuracy (%)',
              font: {
                size: isMobile ? 10 : 12,
              },
              color: '#FFFFFF',
            },
            ticks: {
              font: {
                size: isMobile ? 9 : 11,
              },
              color: '#FFFFFF',
            },
          },
          x: {
            title: {
              display: !isMobile,
              text: 'Month',
              font: {
                size: isMobile ? 10 : 12,
              },
              color: '#FFFFFF',
            },
            ticks: {
              font: {
                size: isMobile ? 9 : 11,
              },
              maxRotation: isMobile ? 45 : 0,
              minRotation: isMobile ? 45 : 0,
              color: '#FFFFFF',
            },
          },
        },
      };

      setChartData(chartData);
      setChartOptions(chartOptions);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-500 bg-opacity-50 z-50 flex justify-center items-center">
        <TailSpin color="#fff" height={50} width={50} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 text-center p-4">
        <i className="pi pi-exclamation-triangle mr-2"></i>
        {error}
      </div>
    );
  }

  const chartTitle = () => {
    let title = `Monthly Trend: ${predictionTypeName}`;
    if (league) title += ` - ${league}`;
    if (country) title += ` (${country})`;
    if (seasonYear) title += ` - Season ${seasonYear}`;

    return isMobile ? (
      <div className="text-center">
        <div>Monthly Trend:</div>
        <div>{predictionTypeName}</div>
        {league && <div>{league}</div>}
        {country && <div>({country})</div>}
        {seasonYear && <div>Season {seasonYear}</div>}
      </div>
    ) : (
      title
    );
  };

  return (
    <Card className="p-2 md:p-4 mb-4 md:mb-8 shadow-md rounded-lg monthly-trend-chart">
      <h3 className="text-base md:text-xl font-semibold mb-2 md:mb-4">
        {chartTitle()}
      </h3>

      <div className="w-full" style={{ height: isMobile ? '300px' : '500px' }}>
        {chartData ? (
          <Chart
            type="line"
            data={chartData}
            options={chartOptions || undefined}
            style={{ height: '100%' }}
            className="monthly-chart"
          />
        ) : (
          <div className="text-center p-4 md:p-8 text-gray-500">
            No data available for the selected filters
          </div>
        )}
      </div>
    </Card>
  );
};

export default MonthlyTrendChart;
